import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { usersApi } from '../../api/users';
import { Mail, Lock, CheckCircle } from 'lucide-react';
import Logo from '../Logo';
import '../../styles/auth-gaming.css';
import { useLanguage } from '../../i18n/LanguageContext';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [step, setStep] = useState(token ? 'reset' : 'request'); // 'request' or 'reset'
  const [formData, setFormData] = useState({
    email: '',
    token: token || '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.email) {
      setError(t('auth.fillAllFields'));
      return;
    }

    setLoading(true);

    try {
      const response = await usersApi.requestPasswordReset(formData.email);
      
      if (response?.success || response?.message) {
        setSuccess(t('auth.requestResetSuccess'));
        // In production, the token would be sent via email
        // For now, we show it for testing (remove in production)
        if (response.token) {
          setSuccess(t('auth.requestResetToken', { token: response.token }));
        }
      }
    } catch (err) {
      // Don't reveal if email exists
      setSuccess(t('auth.requestResetSuccess'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formData.token) {
      setError(t('auth.resetTokenRequired'));
      return;
    }

    if (!formData.newPassword || formData.newPassword.length < 8) {
      setError(t('auth.resetPasswordShort'));
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }

    setLoading(true);

    try {
      const response = await usersApi.resetPassword(formData.token, formData.newPassword);
      
      if (response?.success || response?.message) {
        setSuccess(t('auth.resetSuccess'));
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      }
    } catch (err) {
      setError(err.message || t('auth.resetFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      {/* Auth Background */}
      <div 
        className="auth-background"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 100%), url('/images/login.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat'
        }}
      ></div>
      
      <div className="w-full max-w-md relative z-10">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4" style={{ transform: 'scale(2)' }}>
            <Logo size="xl" showText={false} />
          </div>
        </div>

        {/* Form */}
        <div className="football-card glass-football p-8">
          {step === 'request' ? (
            <>
              <h2 className="text-2xl font-bold mb-6 text-center">{t('auth.forgotTitle')}</h2>
              <p className="text-sm text-gray-300 mb-6 text-center">
                {t('auth.forgotSubtitle')}
              </p>

              {error && (
                <div className="bg-red-50 border border-red-200 p-4 mb-6">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 p-4 mb-6">
                  <p className="text-green-800 text-sm whitespace-pre-wrap">{success}</p>
                </div>
              )}

              <form onSubmit={handleRequestReset} className="space-y-4">
                <div>
                  <label className="block mb-2 flex items-center">
                    <Mail className="w-4 h-4 mr-2" />
                    {t('auth.email')}
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full"
                    placeholder="your.email@example.com"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-football w-full text-lg py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t('auth.sending') : t('auth.sendResetLink')}
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold mb-6 text-center">{t('auth.setNewPassword')}</h2>

              {error && (
                <div className="bg-red-50 border border-red-200 p-4 mb-6">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 p-4 mb-6 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                  <p className="text-green-800 text-sm">{success}</p>
                </div>
              )}

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block mb-2 flex items-center">
                    <Lock className="w-4 h-4 mr-2" />
                    {t('auth.resetToken')}
                  </label>
                  <input
                    type="text"
                    value={formData.token}
                    onChange={(e) => setFormData({ ...formData, token: e.target.value })}
                    className="w-full"
                    placeholder={t('auth.resetToken')}
                    required
                  />
                </div>

                <div>
                  <label className="block mb-2 flex items-center">
                    <Lock className="w-4 h-4 mr-2" />
                    {t('auth.newPassword')}
                  </label>
                  <input
                    type="password"
                    value={formData.newPassword}
                    onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                    className="w-full"
                    placeholder={t('auth.newPassword')}
                    required
                    minLength={8}
                  />
                </div>

                <div>
                  <label className="block mb-2 flex items-center">
                    <Lock className="w-4 h-4 mr-2" />
                    {t('auth.confirmNewPassword')}
                  </label>
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="w-full"
                    placeholder={t('auth.confirmNewPassword')}
                    required
                    minLength={8}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-football w-full text-lg py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? t('auth.resetting') : t('auth.resetPassword')}
                </button>
              </form>
            </>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" className="text-sm text-blue-400 hover:text-blue-300">
              {t('auth.signIn')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
